<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('admin.add_work_shift') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workShift.name"
                :label="$t('admin.shift_name')"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />

        <oxd-form-row>
          <oxd-text class="orangehrm-sub-title"
            >{{ $t('admin.working_hours') }} *</oxd-text
          >
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <time-input
                v-model="workShift.startTime"
                :rules="rules.fromTime"
                :label="$t('general.from')"
              />
            </oxd-grid-item>

            <oxd-grid-item>
              <time-input
                v-model="workShift.endTime"
                :rules="rules.endTime"
                :label="$t('general.to')"
              />
            </oxd-grid-item>

            <oxd-grid-item>
              <oxd-input-group :label="$t('admin.duration_per_day')">
                <oxd-text class="orangehrm-workshift-duration" tag="p">
                  {{ selectedTimeDuration }}
                </oxd-text>
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-text class="orangehrm-sub-title">
            {{ $t('admin.shift_permission_settings') }}
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <time-input
                v-model="workShift.maxLateArrivalTime"
                :rules="rules.maxLateArrivalTime"
                :label="$t('admin.max_late_arrival_time')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <time-input
                v-model="workShift.minEarlyDepartureTime"
                :rules="rules.minEarlyDepartureTime"
                :label="$t('admin.min_early_departure_time')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-text class="orangehrm-sub-title">
            {{ $t('leave.work_week') }}
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item v-for="day in workWeekDays" :key="day.key">
              <oxd-input-field
                v-model="workShift[day.key]"
                type="select"
                :options="dayTypes"
                :rules="rules[day.key]"
                :label="$t(day.label)"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <work-shift-employee-autocomplete
                v-model="workShift.empNumbers"
                :rules="rules.empNumbers"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  validSelection,
  validTimeFormat,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import {diffInDate, parseTime} from '@/core/util/helper/datefns';
import WorkShiftEmployeeAutocomplete from '@/orangehrmAdminPlugin/components/WorkShiftEmployeeAutocomplete';
import {createWorkShiftPermissionTimeRules} from '@/orangehrmAdminPlugin/util/workShiftPermissionTimeRules';
import dayjs from 'dayjs';
const workWeekDays = [
  {key: 'monday', label: 'general.monday'},
  {key: 'tuesday', label: 'general.tuesday'},
  {key: 'wednesday', label: 'general.wednesday'},
  {key: 'thursday', label: 'general.thursday'},
  {key: 'friday', label: 'general.friday'},
  {key: 'saturday', label: 'general.saturday'},
  {key: 'sunday', label: 'general.sunday'},
];

const workShiftModel = {
  id: '',
  name: '',
  hoursPerDay: '',
  startTime: '',
  endTime: '',
  maxLateArrivalTime: '',
  minEarlyDepartureTime: '',
  monday: null,
  tuesday: null,
  wednesday: null,
  thursday: null,
  friday: null,
  saturday: null,
  sunday: null,
  empNumbers: [],
};
export default {
  components: {
    'work-shift-employee-autocomplete': WorkShiftEmployeeAutocomplete,
  },
  props: {
    workShiftConfig: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    dayTypes: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/work-shifts',
    );
    return {
      http,
    };
  },
  data() {
    const permissionTimeRules = createWorkShiftPermissionTimeRules(
      () => this.workShift,
      () => this.$t('admin.shift_permission_time_outside_working_hours'),
    );

    const dayRules = {};
    workWeekDays.forEach((day) => {
      dayRules[day.key] = [required];
    });

    return {
      isLoading: false,
      workWeekDays,
      workShift: {...workShiftModel},
      rules: {
        name: [required, shouldNotExceedCharLength(50)],
        fromTime: [required, validTimeFormat],
        endTime: [required, validTimeFormat],
        maxLateArrivalTime: [required, validTimeFormat, ...permissionTimeRules],
        minEarlyDepartureTime: [
          required,
          validTimeFormat,
          ...permissionTimeRules,
        ],
        empNumbers: [validSelection],
        ...dayRules,
      },
    };
  },
  computed: {
    selectedTimeDuration() {
      // check if startTime is after endTime
      let endTime = parseTime(this.workShift.endTime, 'HH:mm');
      let startTime = parseTime(this.workShift.startTime, 'HH:mm');
      if (startTime > endTime) {
        endTime = dayjs(endTime).add(1, 'day').toDate();
      }
      const diffInSeconds = diffInDate(startTime, endTime);
      const diffHours = parseFloat(diffInSeconds / 3600).toFixed(2);
      return diffHours;
    },
  },
  beforeMount() {
    this.isLoading = true;
    this.workShift.startTime = this.workShiftConfig.startTime;
    this.workShift.endTime = this.workShiftConfig.endTime;
    this.workShift.maxLateArrivalTime =
      this.workShiftConfig.maxLateArrivalTime ?? '10:30';
    this.workShift.minEarlyDepartureTime =
      this.workShiftConfig.minEarlyDepartureTime ?? '15:00';
    const workWeek = this.workShiftConfig.workWeek || {};
    this.workWeekDays.forEach((day) => {
      this.workShift[day.key] = this.dayTypeFor(workWeek[day.key]);
    });
    this.http
      .getAll({limit: 0})
      .then((response) => {
        const {data} = response.data;
        this.rules.name.push((v) => {
          const index = data.findIndex(
            (item) =>
              String(item.name).toLowerCase() == String(v).toLowerCase(),
          );
          return index === -1 || this.$t('general.already_exists');
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    dayTypeFor(length) {
      return this.dayTypes.find((dayType) => dayType.id === length) || null;
    },
    hasWorkingDay() {
      return this.workWeekDays.some(
        (day) => this.workShift[day.key] && this.workShift[day.key].id !== 8,
      );
    },
    onSave() {
      if (!this.hasWorkingDay()) {
        return this.$toast.warn({
          title: this.$t('general.warning'),
          message: this.$t('leave.at_least_one_day_should_be_a_working_day'),
        });
      }

      this.isLoading = true;
      const payload = {
        name: this.workShift.name,
        hoursPerDay: this.selectedTimeDuration,
        startTime: this.workShift.startTime,
        endTime: this.workShift.endTime,
        maxLateArrivalTime: this.workShift.maxLateArrivalTime,
        minEarlyDepartureTime: this.workShift.minEarlyDepartureTime,
        monday: this.workShift.monday.id,
        tuesday: this.workShift.tuesday.id,
        wednesday: this.workShift.wednesday.id,
        thursday: this.workShift.thursday.id,
        friday: this.workShift.friday.id,
        saturday: this.workShift.saturday.id,
        sunday: this.workShift.sunday.id,
        empNumbers: this.workShift.empNumbers.map((employee) => employee.id),
      };
      this.http
        .create(payload)
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.workShift = {...workShiftModel};
          this.onCancel();
        });
    },
    onCancel() {
      navigate('/admin/workShift');
    },
  },
};
</script>
<style src="./work-shift.scss" lang="scss" scoped></style>
